import { Platform } from 'react-native';

import type { MobileWebOs } from '../../onboarding/helpers/detectMobileWebOsFromUserAgent.helper';
import {
  WEB_NATIVE_APP_PROMO_COOLDOWN_HOURS,
  WEB_NATIVE_APP_PROMO_STORAGE_KEYS,
} from '../constants/webNativeAppPromo.constants';

export interface WebNativeAppPromoStorageSnapshot {
  permanentDismissed: boolean;
  lastShownAt: string | null;
  lastDismissedAt: string | null;
}

export interface ShouldShowWebNativeAppPromoInput {
  platform: string;
  mobileOs: MobileWebOs | null;
  isLoaded: boolean;
  isSignedIn: boolean;
  pathname: string;
  storage: WebNativeAppPromoStorageSnapshot;
  now: Date;
}

const MS_PER_HOUR = 60 * 60 * 1000;

function readLocalStorageItem(key: string): string | null {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeLocalStorageItem(key: string, value: string): void {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // private mode / quota — swallow
  }
}

export function isWebNativeAppPromoAppRoute(pathname: string): boolean {
  const segments = pathname.split('/').filter(Boolean);
  return segments[0] === 'app';
}

function parseTimestampMs(value: string | null): number | null {
  if (!value) return null;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? null : parsed;
}

/**
 * Daily cadence: suppressed while the later of last shown / last dismissed is
 * within the cooldown window. Replaces the retired sessionStorage session gate
 * (Android Chrome restores sessionStorage across tab restores → promo never re-appeared).
 */
export function isWebNativeAppPromoCooldownActive(
  lastShownAt: string | null,
  lastDismissedAt: string | null,
  now: Date,
  cooldownHours: number = WEB_NATIVE_APP_PROMO_COOLDOWN_HOURS,
): boolean {
  const latest = Math.max(
    parseTimestampMs(lastShownAt) ?? 0,
    parseTimestampMs(lastDismissedAt) ?? 0,
  );
  if (latest === 0) return false;
  return now.getTime() - latest < cooldownHours * MS_PER_HOUR;
}

export function shouldShowWebNativeAppPromo(input: ShouldShowWebNativeAppPromoInput): boolean {
  if (input.platform !== 'web') return false;
  if (!input.mobileOs) return false;
  if (!input.isLoaded || !input.isSignedIn) return false;
  if (!isWebNativeAppPromoAppRoute(input.pathname)) return false;
  if (input.storage.permanentDismissed) return false;
  if (
    isWebNativeAppPromoCooldownActive(
      input.storage.lastShownAt,
      input.storage.lastDismissedAt,
      input.now,
    )
  ) {
    return false;
  }
  return true;
}

export function readWebNativeAppPromoStorage(): WebNativeAppPromoStorageSnapshot {
  const permanentDismissed =
    readLocalStorageItem(WEB_NATIVE_APP_PROMO_STORAGE_KEYS.PERMANENT_DISMISSED) === 'true';
  const lastShownAt = readLocalStorageItem(WEB_NATIVE_APP_PROMO_STORAGE_KEYS.LAST_SHOWN_AT);
  const lastDismissedAt = readLocalStorageItem(
    WEB_NATIVE_APP_PROMO_STORAGE_KEYS.LAST_DISMISSED_AT,
  );

  return {
    permanentDismissed,
    lastShownAt,
    lastDismissedAt,
  };
}

/** Written when the dialog actually fires — starts the 24h cooldown even if never dismissed. */
export function recordWebNativeAppPromoShown(now: Date = new Date()): void {
  writeLocalStorageItem(WEB_NATIVE_APP_PROMO_STORAGE_KEYS.LAST_SHOWN_AT, now.toISOString());
}

export function recordWebNativeAppPromoDismissal(permanent: boolean, now: Date = new Date()): void {
  writeLocalStorageItem(
    WEB_NATIVE_APP_PROMO_STORAGE_KEYS.LAST_DISMISSED_AT,
    now.toISOString(),
  );
  if (permanent) {
    writeLocalStorageItem(WEB_NATIVE_APP_PROMO_STORAGE_KEYS.PERMANENT_DISMISSED, 'true');
  }
}
